
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { Box, Button, MenuItem, Paper, Select, Typography, styled } from '@mui/material';
import { FC, useState, useCallback, useRef, useMemo } from 'react';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Document, Page, pdfjs } from 'react-pdf';
import { Waypoint } from 'react-waypoint';
import { useParams } from 'react-router-dom';
import { RouteLoading } from '@ngt/opms';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();


const StyledContainer = styled(Box)(() => ({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'stretch',
    height: '100vh'
}));

const StyledTopContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 64,
    minHeight: 64,
    maxHeight: 64,
    color: '#FFFFFF',
    background: '#38383d',
    flex: '0 0 auto'
}));

const StyledPageNumberContainer = styled(Box)(() => ({
    display: "flex",
    flex: '1 1 auto',
    alignItems: "center",
    marginLeft: "8px"
}));


const StyledDocumentContainer = styled(Box)(() => ({
    background: '#2a2a2e',
    overflow: 'auto',
    flex: '1 1 auto',
    scrollbarColor: '#88888b #38383d'
}));

const StyledPageContainer = styled(Box)(() => ({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
}));

const StyledButton = styled(Button)(() => ({
    color: '#FFFFFF',
    fontSize: 20
}));

const StyledSelect = styled(Select)(() => ({
    flex: '0 0 auto',
    color: '#FFFFFF',
    marginRight: '8px'
}));

export interface IPdfViewer {
    url?: string;
    file?: File;
}

const PdfViewer: FC<IPdfViewer> = ({
    url,
    file
}) => {
    
    const [numPages, setNumPages] = useState(0);
    const [pages, setPages] = useState<number[]>([]);
    const [page, setPage] = useState(0);
    const [pageWidths, setPageWidths] = useState<Record<number, number>>({})
    const [pageFit, setPageFit] = useState(false);
    const [scale, setScale] = useState(1.0);
    const containerRef = useRef<HTMLDivElement>(null);

    const scales = useMemo(() => {
        const s = new Set([
            0.5,
            1,
            1.5,
            2,
            2.5,
            3
        ])
    
        s.add(scale);
    
        return Array.from(s).sort((a,b) => {
            return a < b ? -1 : 1;
        })
    }, [scale])

    const onDocumentLoadSuccess = useCallback(({ numPages }: any): void => {
        setNumPages(numPages);
    
        setPages(Array.from({length: numPages}, (_, i) => i + 1));

        setPage(1);
    
    }, [setNumPages, setPages, setPage]);

    const onPageLoadSuccess = useCallback(({ originalWidth, pageNumber }: any): void => {
        setPageWidths(c => {
            return {
                ...c,
                [pageNumber]: originalWidth
            }
        })
    }, [setPageWidths]);

    const onZoomInClick = useCallback(() => {
        setPageFit(false);
        setScale((s) => {
            const value: number = +(s+(Math.ceil(s) / 10)).toFixed(2);

            if (value > 10) {
                return 10;
            }
            return value;
        })
    }, [setPageFit, setScale]);

    const onZoomOutClick = useCallback(() => {
        setPageFit(false);
        setScale((s) => {
            const value: number = +(s-(Math.ceil(s) / 10)).toFixed(2);
    
            if (value < 0.1) {
                return 0.1;
            }
            return value;
        })
    }, [setPageFit, setScale]);
    
    return (
        <StyledContainer>
            <StyledTopContainer>
                <StyledPageNumberContainer>
                    <Typography>
                        Page {page} of {numPages}
                    </Typography>
                </StyledPageNumberContainer>
                <StyledButton
                    disabled={scale === 0.1}
                    onClick={onZoomOutClick}
                >
                    -
                </StyledButton>
                <StyledButton
                    disabled={scale === 0.1}
                    onClick={onZoomInClick}
                >
                    +
                </StyledButton>
                <StyledSelect
                    IconComponent={() => (
                        <FontAwesomeIcon
                            icon={faChevronDown}
                        />
                    )}
                    value={pageFit ? "Fit" : scale.toString()}
                    label="Scale" 
                    onChange={(e) => {
                        const v = e.target.value as string;
                        if (v === "Fit")
                        {
                            const pageFitWidth = (containerRef.current?.offsetWidth ?? 850) - 48;
                            const originalWidth = pageWidths[page] ?? 850;
                    
                    
                            setPageFit(true);
                            setScale(+((pageFitWidth / originalWidth) * 10).toFixed(0) / 10)
                        }
                        else
                        {
                            setPageFit(false);
                            setScale(+(e.target.value as any))
                        }
                    }}
                >
                    <MenuItem value="Fit">Fit To Page</MenuItem>
                    {
                        scales.map((s) => {
                            return (
                                <MenuItem value={s.toString()}>{(s*100).toFixed(0)}%</MenuItem>
                            );

                        })
                    }
                </StyledSelect>
            </StyledTopContainer>
            <StyledDocumentContainer
                ref={containerRef}
            >
                <Document
                    file={file ?? url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error: any) => alert('An error occurred while loading the file: ' + error.message)}
                    loading={<RouteLoading/>}
                    error={<StyledPageContainer><Typography variant='h6' margin={5}>Failed to load PDF file.</Typography></StyledPageContainer>}
                >
                    <StyledPageContainer>
                    {
                        pages.map(x => (
                            <Waypoint
                                key={x}
                                topOffset={"49%"}
                                bottomOffset={"49%"}
                                onEnter={({ event }: any) => {
                                    if (event != null) 
                                    {
                                        setPage(x);
                                    }
                                }}
                            >
                                <Paper
                                    key={x}
                                    style={{marginBottom: 8, marginTop: 8, overflow: 'hidden'}}
                                >
                                    <Page
                                        pageNumber={x}
                                        onLoadSuccess={onPageLoadSuccess}
                                        scale={scale}
                                        renderAnnotationLayer={false}
                                        renderTextLayer={false}
                                    />
                                </Paper>
                            </ Waypoint>
                        ))
                    }
                    </StyledPageContainer>
                </Document>
            </StyledDocumentContainer>
        </StyledContainer>
    )
}

export const PdfViewerPage: FC = () => {
    const params = useParams();

    const url = useMemo(() => {
        if(params?.guid) {
            return `/opms/file-upload/download/${params?.guid}`
        } else {
            return `/opms/file/download/${params?.fileId}`
        }
    }, [params])

    return (
        <>
            <PdfViewer url={url} />
        </>
    )
}

export default PdfViewer;